import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, Film, MapPin, BookOpen, Heart, Plane } from "lucide-react";
import usePageMeta from "@/lib/usePageMeta";

/* ---------- NC500 launch countdown ---------- */
const NC500_LAUNCH = new Date("2026-11-06T06:00:00Z"); // dawn UTC

function useCountdown(target) {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);
    const diff = Math.max(0, target.getTime() - now.getTime());
    const s = Math.floor(diff / 1000);
    return {
        totalSeconds: s,
        days: Math.floor(s / 86400),
        hours: Math.floor((s % 86400) / 3600),
        minutes: Math.floor((s % 3600) / 60),
        seconds: s % 60,
        launched: diff === 0,
    };
}

/* ---------- Milestone-driven hero copy ---------- */
/**
 * Tiers are evaluated top → bottom; first match wins.
 * `chapter` is the small overline label above the h1.
 * Headline uses three tokens rendered as: TOKEN[0] · italic-brand TOKEN[1] · TOKEN[2]
 */
const MILESTONES = [
    {
        id: "launched",
        match: (c) => c.launched,
        chapter: "The Mission — Live",
        headline: ["Wheels", "up.", "The Beast is airborne."],
        sub: "The NC500 expedition is underway. Follow the flight log in real time.",
    },
    {
        id: "final-day",
        match: (c) => !c.launched && c.days < 1,
        chapter: "Final Countdown · T-24 Hours",
        headline: ["Wheels up in", "twenty-four", "hours."],
        sub: "SD cards formatted. Batteries charged. The wind is picking up over the Highlands.",
    },
    {
        id: "final-week",
        match: (c) => c.days < 7,
        chapter: "T-Minus One Week",
        headline: ["Final", "week.", "The Beast is fuelled."],
        sub: "Seven days until 516 miles of coastline, castles and open sky.",
    },
    {
        id: "thirty",
        match: (c) => c.days < 30,
        chapter: "T-Minus 30 Days",
        headline: ["Thirty days", "to", "takeoff."],
        sub: "One month out from the NC500. The maps are marked. The mission is set.",
    },
    {
        id: "hundred",
        match: (c) => c.days < 100,
        chapter: "T-Minus 100 Days",
        headline: ["One hundred", "days,", "one horizon."],
        sub: "The countdown to Scotland's most cinematic 516 miles begins here.",
    },
    {
        id: "default",
        match: () => true,
        chapter: "Start Here — Chapter 00",
        headline: ["What", "DTScotslad", "is."],
        sub: "A journey of reconstruction — filmed from four hundred feet.",
    },
];

function useActiveMilestone(countdown) {
    return MILESTONES.find((m) => m.match(countdown)) || MILESTONES[MILESTONES.length - 1];
}

function LaunchTicker({ countdown }) {
    const { days, hours, minutes, seconds, launched } = countdown;
    const pad = (n) => String(n).padStart(2, "0");

    const units = [
        { label: "Days", value: pad(days) },
        { label: "Hrs", value: pad(hours) },
        { label: "Min", value: pad(minutes) },
        { label: "Sec", value: pad(seconds) },
    ];

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 lg:mt-8 inline-flex flex-col gap-3"
            data-testid="nc500-countdown"
        >
            <div className="flex items-center gap-3 text-[var(--sb-brand)]">
                <span className="w-2 h-2 rounded-full bg-[var(--sb-brand)] animate-pulse" />
                <span className="text-[0.7rem] tracking-[0.32em] uppercase">
                    {launched ? "The NC500 mission has launched" : "NC500 Launch · 6 Nov 2026"}
                </span>
            </div>
            <div className="flex items-end gap-6 lg:gap-9 border-t border-[var(--sb-border)] pt-4 pr-2">
                {units.map((u) => (
                    <div key={u.label} className="flex flex-col items-start" data-testid={`countdown-${u.label.toLowerCase()}`}>
                        <span
                            className="font-serif text-[var(--sb-text)] tabular-nums leading-none"
                            style={{ fontSize: "clamp(2.2rem, 4.5vw, 3.8rem)" }}
                        >
                            {u.value}
                        </span>
                        <span className="mt-2 text-[0.65rem] tracking-[0.32em] uppercase text-[var(--sb-text-muted)]">
                            {u.label}
                        </span>
                    </div>
                ))}
            </div>
        </motion.div>
    );
}

/* ---------- Content ---------- */
const HERO_IMG =
    "https://images.pexels.com/photos/29353711/pexels-photo-29353711.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=2000";
const CHAPTER_IMG_A =
    "https://images.pexels.com/photos/10677765/pexels-photo-10677765.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=800";
const CHAPTER_IMG_B =
    "https://images.pexels.com/photos/6280898/pexels-photo-6280898.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=800";
const CHAPTER_IMG_C =
    "https://images.pexels.com/photos/16948691/pexels-photo-16948691.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=800";

const CHAPTERS = [
    {
        n: "01",
        title: "The Silence After the Storm",
        image: CHAPTER_IMG_A,
        body: [
            "They say life can change in an instant. For me it changed in two brutal waves. To understand what DTScotslad is, you have to understand the silence that came before the first flight — the four walls of a hospital room and the rhythmic beep of monitors.",
            "In 2008, pancreatitis nearly took me out of the game. It left me with Type 1 Diabetes and a body relearning the rules of survival. Then in 2023, during a routine operation at Ninewells, my heart stopped. A massive allergic reaction to an anaesthetic agent. The CPR that saved my life broke seven of my ribs.",
            "I woke up in the ICU, tethered to tubes, feeling the weight of a body playing hardball. Neuropathy numbing my fingers. Splints keeping my legs moving. And the rugged Scotland I loved — the glens, the mist-heavy lochs, the ancient stone sentinels — felt like a fortress I could no longer enter.",
            "But when the ground is too rough for a takeoff, you don't give up. You rebuild the engine.",
        ],
    },
    {
        n: "02",
        title: "The First Waypoint — Zero Minors",
        image: CHAPTER_IMG_B,
        body: [
            "The reconstruction began on the tarmac. In June 2026 I sat for my driving test. For someone dealing with the daily palava of chronic pain, getting back behind the wheel was about more than transport — it was the first taste of reclaimed independence.",
            "I didn't just pass. I passed with zero minors.",
            "That result was the proof. Despite the pancreatitis, despite the cardiac arrest, despite the motorbikes of a failing digestive system — I could still hit the professional standard. Still be precise. Still be a pilot. That licence became the foundation of the brand you see today.",
        ],
    },
    {
        n: "03",
        title: "Digital Wings — The Arrival of the Beast",
        image: CHAPTER_IMG_C,
        body: [
            "DTScotslad was born from a realisation: technology could be a mobility aid for the soul. If my legs couldn't take me to the battlements of Balvaird, my vision still could.",
            "I invested in the DJI Air 3 — a 720g dual-camera cinematic powerhouse I call The Beast. A wide-angle for the sweeping splendor of the glens. A 70mm telephoto for the compressed drama of ancestral masonry.",
            "When the props spin, the neuropathy in my hands doesn't matter. Neither do the splints. For those 40 minutes of flight time, I am four hundred feet in the air, seeing Scotland with a clarity most people never touch.",
        ],
    },
    {
        n: "04",
        title: "Why Disabled Touring Matters",
        image: CHAPTER_IMG_A,
        body: [
            "Too often, accessibility is discussed only in ramps and elevators. But what about accessibility to the sublime? What about the right to see the sun rise over a remote Highland fortress?",
            "Disabled Touring is about showing that our perspective isn't restricted just because our bodies are. When I film Elcho Castle, I'm not just chasing a pretty shot — I'm looking at the resilience of the stone. The way it has survived centuries of weather and war, much like I've survived the trials of the last two decades.",
            "This is a community for those who know what it's like to fight for every inch of progress. For the dreamers tired of being told No Access. Here — we fly over the gates.",
        ],
    },
    {
        n: "05",
        title: "The Mission — NC500 & The Legacy",
        image: CHAPTER_IMG_B,
        body: [
            "On November 6, 2026 I am embarking on my most ambitious mission yet: a 516-mile expedition of the NC500.",
            "This isn't just about 4K footage. It's a fundraiser for the MS Society. The goal is £1,800 to fund fuel and equipment — with every penny beyond that going directly to those fighting Multiple Sclerosis.",
            "I want to build a legacy that shows my nieces and nephews — and anyone at a crossroads — that the only limit is the one you accept. No follow-for-follow fluff. Just the raw graft: the all-nighters, the palava of managing SD cards with numb fingers, and the pure, unadulterated splendor of Scotland.",
        ],
    },
];

const START_LINKS = [
    { to: "/gallery", icon: Film, k: "01", t: "Watch", d: "Cinematic 4K flights across the Highlands, Islands and Cities." },
    { to: "/map", icon: MapPin, k: "02", t: "Explore", d: "Every flight pinned across Scotland — see where the Beast has been." },
    { to: "/blog", icon: BookOpen, k: "03", t: "Read", d: "Field notes, the Pilot's Log, and the story behind the shots." },
    { to: "/subscription", icon: Heart, k: "04", t: "Back", d: "Subscribe for £1.89 or fund the NC500 for the MS Society." },
];

/* ---------- Motion primitives ---------- */
const easeOut = [0.16, 1, 0.3, 1];

// Masked, per-word line reveal
function MaskedLine({ children, delay = 0, className = "", dataTestid }) {
    return (
        <span
            className="inline-block overflow-hidden align-bottom"
            style={{ lineHeight: 1, paddingRight: "0.08em", paddingBottom: "0.05em" }}
        >
            <motion.span
                data-testid={dataTestid}
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, delay, ease: easeOut }}
                className={`inline-block ${className}`}
            >
                {children}
            </motion.span>
        </span>
    );
}

function Reveal({ children, delay = 0, y = 24, className = "" }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-15% 0px -10% 0px" });
    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, y }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay, ease: easeOut }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

/* ---------- Sections ---------- */
function Hero() {
    const ref = useRef(null);
    const countdown = useCountdown(NC500_LAUNCH);
    const milestone = useActiveMilestone(countdown);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
    const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
    const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.2]);

    return (
        <section
            ref={ref}
            className="relative min-h-[100svh] w-full overflow-hidden grain"
            data-testid="start-hero"
            data-milestone={milestone.id}
        >
            {/* Parallax image */}
            <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
                <img
                    src={HERO_IMG}
                    alt="Highland glen from above"
                    className="w-full h-full object-cover slow-zoom-ne"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[var(--sb-bg)]/45 via-[var(--sb-bg)]/10 to-[var(--sb-bg)]" />
                {/* Radial spotlight */}
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "radial-gradient(60% 45% at 50% 42%, rgba(255,240,220,0.10) 0%, rgba(0,0,0,0) 60%)",
                    }}
                />
            </motion.div>

            {/* Top meta row */}
            <motion.div
                style={{ opacity }}
                className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-10 flex items-center justify-between text-xs tracking-[0.28em] uppercase text-[var(--sb-text-dim)]"
            >
                <span className="flex items-center gap-2">
                    <Plane size={12} className="text-[var(--sb-brand)]" />
                    <MaskedLine delay={0.05}>Pilot&rsquo;s&nbsp;Manifesto</MaskedLine>
                </span>
                <span className="hidden sm:inline"><MaskedLine delay={0.15}>2026 · SCOTLAND · 4K</MaskedLine></span>
            </motion.div>

            {/* Headline */}
            <div className="relative z-10 min-h-[calc(100svh-4rem)] max-w-7xl mx-auto px-6 lg:px-12 flex flex-col justify-end pt-8 pb-14 lg:pb-16">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={milestone.id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.6, ease: easeOut }}
                        data-testid={`milestone-${milestone.id}`}
                    >
                        <div className="overline mb-3">
                            <MaskedLine delay={0.1}>{milestone.chapter}</MaskedLine>
                        </div>

                        <h1
                            className="font-serif font-light tracking-tighter"
                            style={{
                                fontSize: "clamp(2.8rem, 8vw, 8rem)",
                                lineHeight: 0.92,
                            }}
                            data-testid="start-hero-title"
                        >
                            <div>
                                <MaskedLine delay={0.15}>{milestone.headline[0]}</MaskedLine>{" "}
                                <MaskedLine delay={0.25}>
                                    <span className="italic text-[var(--sb-brand)]">{milestone.headline[1]}</span>
                                </MaskedLine>
                            </div>
                            <div>
                                <MaskedLine delay={0.4}>{milestone.headline[2]}</MaskedLine>
                            </div>
                        </h1>

                        <div className="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                            <div className="max-w-md">
                                <MaskedLine delay={0.6} className="text-lg lg:text-xl text-[var(--sb-text-dim)] leading-relaxed">
                                    {milestone.sub}
                                </MaskedLine>
                            </div>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.9, delay: 0.9, ease: easeOut }}
                                className="flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-[var(--sb-text-muted)]"
                            >
                                <span className="w-10 h-px bg-[var(--sb-brand)]" />
                                Scroll to fly
                            </motion.div>
                        </div>
                    </motion.div>
                </AnimatePresence>

                <LaunchTicker countdown={countdown} />
            </div>
        </section>
    );
}

function Marquee() {
    // A single slow editorial marquee — repeats content twice for seamless loop
    const items = ["Get it did.", "·", "Zero Minors.", "·", "The Beast is airborne.", "·", "NC500 — 516 miles.", "·"];
    const track = [...items, ...items, ...items, ...items];

    return (
        <section
            className="relative py-16 lg:py-24 overflow-hidden border-y border-[var(--sb-border)]"
            style={{ background: "var(--sb-surface)" }}
            data-testid="start-marquee"
        >
            <motion.div
                aria-hidden
                className="flex whitespace-nowrap gap-10 font-serif italic tracking-tight text-[var(--sb-text)]"
                style={{ fontSize: "clamp(3rem, 9vw, 8rem)", lineHeight: 1 }}
                animate={{ x: ["0%", "-50%"] }}
                transition={{ duration: 40, ease: "linear", repeat: Infinity }}
            >
                {track.map((t, i) => (
                    <span key={i} className={i % 2 === 0 ? "text-[var(--sb-text)]" : "text-[var(--sb-brand)]"}>
                        {t}
                    </span>
                ))}
            </motion.div>
        </section>
    );
}

function QuickCompass() {
    return (
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="start-compass">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 items-end">
                <Reveal className="lg:col-span-7">
                    <div className="overline mb-3">The Compass</div>
                    <h2 className="font-serif text-4xl lg:text-6xl tracking-tight leading-[0.95]">
                        New here?<br />
                        <span className="italic text-[var(--sb-brand)]">Point your compass.</span>
                    </h2>
                </Reveal>
                <Reveal delay={0.15} className="lg:col-span-5 text-[var(--sb-text-dim)] text-lg leading-relaxed">
                    Four ways to enter the airspace. Pick whichever pulls you in — the rest will find you as you go.
                </Reveal>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                {START_LINKS.map((l, i) => {
                    const Icon = l.icon;
                    return (
                        <Reveal key={l.to} delay={0.1 + i * 0.08}>
                            <Link
                                to={l.to}
                                data-testid={`start-link-${l.to.slice(1)}`}
                                className="relative block h-full border border-[var(--sb-border)] bg-[var(--sb-surface)] p-7 transition-colors duration-500 hover:border-[var(--sb-brand)] group overflow-hidden"
                            >
                                <div className="absolute -top-6 -right-6 text-[8rem] font-serif italic leading-none text-[var(--sb-border)] group-hover:text-[var(--sb-brand)]/25 transition-colors duration-500">
                                    {l.k}
                                </div>
                                <div className="relative z-10">
                                    <Icon className="text-[var(--sb-brand)] mb-6" size={22} />
                                    <div className="overline mb-2">{l.k} · Chapter</div>
                                    <h3 className="font-serif text-3xl mb-3 tracking-tight">{l.t}</h3>
                                    <p className="text-sm text-[var(--sb-text-dim)] leading-relaxed">{l.d}</p>
                                    <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[var(--sb-brand)] mt-8 group-hover:gap-3 transition-all">
                                        Enter <ArrowUpRight size={13} />
                                    </span>
                                </div>
                            </Link>
                        </Reveal>
                    );
                })}
            </div>
        </section>
    );
}

function Chapter({ chapter, index }) {
    const flip = index % 2 === 1;
    const imgRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

    return (
        <section
            className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-32"
            data-testid={`chapter-${chapter.n}`}
        >
            <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start ${flip ? "lg:[&>*:first-child]:order-2" : ""}`}>
                {/* Image column with clipped/spotlight frame */}
                <Reveal className="lg:col-span-5" delay={0.05}>
                    <div
                        ref={imgRef}
                        className="relative aspect-[3/4] overflow-hidden border border-[var(--sb-border)]"
                        style={{
                            clipPath:
                                index % 2 === 0
                                    ? "polygon(0 0, 100% 0, 100% 92%, 92% 100%, 0 100%)"
                                    : "polygon(8% 0, 100% 0, 100% 100%, 0 100%, 0 8%)",
                        }}
                    >
                        <motion.img
                            src={chapter.image}
                            alt={chapter.title}
                            style={{ y }}
                            className="absolute inset-0 w-full h-[115%] object-cover will-change-transform"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[var(--sb-bg)]/70 via-transparent to-transparent" />
                        <div
                            className="absolute inset-0"
                            style={{
                                background:
                                    "radial-gradient(45% 35% at 30% 25%, rgba(255,240,220,0.15) 0%, rgba(0,0,0,0) 65%)",
                            }}
                        />
                        <div className="absolute left-5 bottom-5 right-5 flex items-end justify-between">
                            <span className="text-xs tracking-[0.28em] uppercase text-[var(--sb-text)]">Chapter</span>
                            <span
                                className="font-serif italic leading-none text-[var(--sb-text)]"
                                style={{ fontSize: "clamp(3.5rem, 7vw, 6rem)" }}
                            >
                                {chapter.n}
                            </span>
                        </div>
                    </div>
                </Reveal>

                {/* Text column */}
                <div className="lg:col-span-7">
                    <Reveal>
                        <div className="flex items-center gap-4 mb-6">
                            <span className="w-12 h-px bg-[var(--sb-brand)]" />
                            <span className="text-xs tracking-[0.28em] uppercase text-[var(--sb-brand)]">
                                {chapter.n} / {String(CHAPTERS.length).padStart(2, "0")}
                            </span>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h3
                            className="font-serif tracking-tight leading-[1.02] mb-8"
                            style={{ fontSize: "clamp(2.2rem, 4.5vw, 4rem)" }}
                        >
                            {chapter.title}
                        </h3>
                    </Reveal>
                    <div className="space-y-6 text-[var(--sb-text-dim)] text-lg leading-[1.85] max-w-2xl">
                        {chapter.body.map((p, i) => (
                            <Reveal key={i} delay={0.15 + i * 0.05}>
                                <p>{p}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

function Closing() {
    return (
        <section className="max-w-5xl mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="start-closing">
            <Reveal>
                <div className="overline mb-4">A Message from the Pilot</div>
            </Reveal>
            <Reveal delay={0.1}>
                <h2
                    className="font-serif tracking-tight leading-[1.02] mb-10"
                    style={{ fontSize: "clamp(2.4rem, 5.5vw, 5rem)" }}
                >
                    The gate is only down if you<br />
                    <span className="italic text-[var(--sb-brand)]">choose not to fly over it.</span>
                </h2>
            </Reveal>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                <Reveal className="lg:col-span-8 text-[var(--sb-text-dim)] text-lg leading-[1.85] space-y-5">
                    <p>
                        I am Mark Ireland. A survivor, a driver with zero minors, and the pilot of DTScotslad. I&rsquo;ve spent too many years waiting for the green light from life, only to realise I had to be the one to hit the switch.
                    </p>
                    <p>
                        Whether you&rsquo;re here for the history, the tech, or the story of the fight — I invite you to join the flight. The splendor of Scotland belongs to all of us. Through these digital wings, we are taking it back — one sweep, one glen, one castle at a time.
                    </p>
                    <p className="font-serif italic text-3xl text-[var(--sb-text)] pt-2">Get it did.</p>
                </Reveal>

                <Reveal delay={0.2} className="lg:col-span-4 flex flex-col gap-4">
                    <a
                        href="https://gofund.me/da1365285"
                        target="_blank"
                        rel="noreferrer"
                        className="btn-primary justify-center"
                        data-testid="start-back-expedition"
                    >
                        Back the Expedition <ArrowRight size={18} />
                    </a>
                    <Link to="/gallery" className="btn-outline justify-center" data-testid="start-watch-flights">
                        Watch the flights
                    </Link>
                    <Link to="/subscription" className="btn-outline justify-center" data-testid="start-subscribe">
                        Subscribe — £1.89 / mo
                    </Link>
                </Reveal>
            </div>
        </section>
    );
}

/* ---------- Page ---------- */
export default function StartHere() {
    usePageMeta({
        title: "Start Here — DTScotslad",
        description: "What DTScotslad is — a journey of reconstruction, filmed from four hundred feet. The pilot's manifesto, in five chapters.",
        ogImage: HERO_IMG,
    });
    return (
        <div data-testid="start-here-page" className="relative">
            <Hero />
            <QuickCompass />
            <Marquee />
            {CHAPTERS.map((c, i) => (
                <Chapter key={c.n} chapter={c} index={i} />
            ))}
            <Closing />
        </div>
    );
}
